import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Modal } from '../../components';
import { ChevronLeftIcon, ChevronRightIcon, ClipboardIcon, CloseLineIcon, EditSquareIcon, FileTextIcon, PlayCircleIcon, PlusIcon, SlideIcon } from '../../layout/icons';
import { useBoardContext } from '../useBoardContext';
import { useClearUnit, useSelectUnit } from '../useSelectUnit';
import { DOCUMENT_KIND_LABEL, useDynamicDocumentStore, type BoardDocument } from './documents';
import { openedDocuments, periodKey, useOpenedDocumentStore, visibleDocuments } from './opened';
import { stripOverflow, stripPageTarget } from './overflow';
import { documentThumbnail } from './thumbnails';

/** Scroll state of the track: how many cells sit off each edge, and how to move a page. */
function useStripOverflow() {
  const ref = useRef<HTMLDivElement | null>(null);
  const [before, setBefore] = useState(0);
  const [after, setAfter] = useState(0);

  const measure = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const cells: { left: number; right: number }[] = [];
    for (let i = 0; i < el.children.length; i += 1) {
      const cell = el.children[i] as HTMLElement;
      cells.push({ left: cell.offsetLeft, right: cell.offsetLeft + cell.offsetWidth });
    }
    const counts = stripOverflow(cells, el.scrollLeft, el.clientWidth);
    setBefore(counts.before);
    setAfter(counts.after);
  }, []);

  const page = useCallback((direction: number) => {
    const el = ref.current;
    if (!el) return;
    // Chrome 58 has no smooth scrollTo(options), so this is a plain assignment
    el.scrollLeft = stripPageTarget(el.scrollLeft, el.clientWidth, el.scrollWidth, direction);
    measure();
  }, [measure]);

  useEffect(() => {
    measure();
    window.addEventListener('resize', measure, false);
    return () => window.removeEventListener('resize', measure, false);
  }, [measure]);

  return { ref, before, after, measure, page };
}

function KindGlyph({ kind, upload }: { kind: BoardDocument['kind']; upload?: boolean }) {
  if (upload) return <FileTextIcon size={20} />;
  if (kind === 'video') return <PlayCircleIcon size={20} />;
  if (kind === 'quiz') return <ClipboardIcon size={20} />;
  return <SlideIcon size={20} />;
}

/**
 * Tappable overflow control.
 *
 * The old gradient was `pointer-events: none`, so the only way to reach an off-screen document was
 * to drag-scroll a strip of thumbnails with a finger on a wall panel. The count on the face answers
 * "how much more" where the affordance already is, instead of spending strip width on a readout.
 */
function StripNav({ side, count, onClick }: { side: 'start' | 'end'; count: number; onClick: () => void }) {
  if (count === 0) return null;
  const label = side === 'start'
    ? `Xem ${count} tài liệu trước đó`
    : `Xem ${count} tài liệu tiếp theo`;
  return (
    <button
      type="button"
      className={`filmstrip__nav filmstrip__nav--${side}`}
      aria-label={label}
      title={label}
      onClick={onClick}
    >
      <span className="filmstrip__nav-glyph" aria-hidden={true}>
        {side === 'start' ? <ChevronLeftIcon size={22} /> : <ChevronRightIcon size={22} />}
      </span>
      <span className="filmstrip__nav-count" aria-hidden={true}>{count}</span>
    </button>
  );
}

export interface FilmstripProps {
  /** The `?unit=` value the canvas is rendering. */
  activeId: string | null;
  /** Title of that unit, used until the loader has recorded it for the period. */
  activeTitle?: string;
  /** Opens the rail's source panels, where picking an item adds it to the strip. */
  onAdd?: () => void;
}

// Switches whole documents, not pages of one deck - the set is heterogeneous by design.
export function Filmstrip({ activeId, activeTitle, onAdd }: FilmstripProps) {
  const selectUnit = useSelectUnit();
  const clearUnit = useClearUnit();
  const { ref, before, after, measure, page } = useStripOverflow();
  const { date, index, classId } = useBoardContext();
  const openedRows = useOpenedDocumentStore((state) => state.rows);
  const hidden = useOpenedDocumentStore((state) => state.hidden);
  const removeFromStrip = useOpenedDocumentStore((state) => state.remove);
  const allGenerated = useDynamicDocumentStore((state) => state.items);
  const [pending, setPending] = useState<BoardDocument | null>(null);
  const period = periodKey(date, index, classId);

  // Bảng trắng, then the units opened this period in open order, then the teacher's generated quizzes
  const opened = useMemo(() => openedDocuments(openedRows, period), [openedRows, period]);
  const generated = useMemo(() => visibleDocuments(allGenerated, hidden, period), [allGenerated, hidden, period]);
  const listed = opened.concat(generated);
  // the unit on screen shows even before its fetch lands and the loader records it
  const known = !activeId || listed.filter((item) => item.id === activeId).length > 0;
  const items = known
    ? listed
    : opened.concat([{ id: activeId, title: activeTitle || 'Bài giảng đang mở', kind: 'slide', pageCount: 1 }]).concat(generated);

  // the blank board is the app's own no-lecture state (documentKey `blank`), not a synthetic
  // document: selecting it clears `?unit=` and the board goes back to an appendable scratch surface
  const blankActive = !activeId;

  // quiz tiles are painted once per id and cached, so scrolling the strip never repaints a page
  const itemKey = items.map((item) => item.id).join('|');
  const thumbnails = useMemo(() => items.map((item) => documentThumbnail(item)), [itemKey]);

  // a new document changes the cell count, so the overflow counts have to be re-measured
  useEffect(() => { measure(); }, [itemKey, measure]);

  // only this period's strip changes: the unit, the uploaded file, the questions and the ink all stay
  const confirmRemove = () => {
    if (!pending) return;
    const isGenerated = allGenerated.filter((item) => item.id === pending.id).length > 0;
    if (pending.id === activeId) clearUnit();
    removeFromStrip(period, pending.id, isGenerated);
    setPending(null);
  };

  return (
    <div className="filmstrip">
      <div
        className="filmstrip__track"
        ref={ref}
        onScroll={measure}
        role="list"
        aria-label="Tài liệu đang mở"
      >
        <div className="filmstrip__cell" role="listitem" key="blank-board">
          <button
            type="button"
            className={blankActive ? 'filmstrip__item is-active' : 'filmstrip__item'}
            aria-current={blankActive ? 'true' : undefined}
            aria-label="Bảng trắng: bảng chưa chèn bài, dùng để viết và vẽ tự do"
            title="Bảng trắng"
            onClick={clearUnit}
          >
            <span className="filmstrip__frame">
              <span className="filmstrip__placeholder filmstrip__placeholder--blank">
                <span className="filmstrip__glyph" aria-hidden={true}><EditSquareIcon size={20} /></span>
                <span className="filmstrip__caption">Bảng trắng</span>
              </span>
            </span>
          </button>
        </div>
        {items.map((item, index) => {
          const active = item.id === activeId;
          const thumbnail = thumbnails[index];
          return (
            <div className="filmstrip__cell" role="listitem" key={item.id}>
              <button
                type="button"
                className={active ? 'filmstrip__item is-active' : 'filmstrip__item'}
                aria-current={active ? 'true' : undefined}
                aria-label={`${item.upload ? 'Tệp tải lên' : DOCUMENT_KIND_LABEL[item.kind]}: ${item.title}`}
                title={item.title}
                onClick={() => selectUnit({ unitId: item.id, unitName: item.title })}
              >
                <span className="filmstrip__frame">
                  {thumbnail ? (
                    <img src={thumbnail} alt="" />
                  ) : (
                    <span className={`filmstrip__placeholder filmstrip__placeholder--${item.kind}`}>
                      <span className="filmstrip__glyph" aria-hidden={true}><KindGlyph kind={item.kind} upload={Boolean(item.upload)} /></span>
                      <span className="filmstrip__caption">{item.title}</span>
                    </span>
                  )}
                </span>
              </button>
              <button
                type="button"
                className="filmstrip__remove"
                aria-label={`Gỡ khỏi dải bài: ${item.title}`}
                title="Gỡ khỏi dải bài"
                onClick={() => setPending(item)}
              >
                <span className="filmstrip__remove-dot" aria-hidden={true}><CloseLineIcon size={14} /></span>
              </button>
            </div>
          );
        })}
        {onAdd ? (
          <div className="filmstrip__cell" role="listitem" key="add">
            <button
              type="button"
              className="filmstrip__item filmstrip__item--add"
              aria-label="Thêm vào dải: mở Kho bài"
              title="Thêm vào dải"
              onClick={onAdd}
            >
              <span className="filmstrip__frame filmstrip__frame--add">
                <span className="filmstrip__placeholder">
                  <span className="filmstrip__glyph" aria-hidden={true}><PlusIcon size={22} /></span>
                  <span className="filmstrip__caption">Thêm vào dải</span>
                </span>
              </span>
            </button>
          </div>
        ) : null}
      </div>
      <span className={before > 0 ? 'filmstrip__fade filmstrip__fade--start is-on' : 'filmstrip__fade filmstrip__fade--start'} aria-hidden={true} />
      <span className={after > 0 ? 'filmstrip__fade filmstrip__fade--end is-on' : 'filmstrip__fade filmstrip__fade--end'} aria-hidden={true} />
      <StripNav side="start" count={before} onClick={() => page(-1)} />
      <StripNav side="end" count={after} onClick={() => page(1)} />
      <Modal
        open={Boolean(pending)}
        title="Gỡ khỏi dải bài?"
        className="page-delete-modal"
        onClose={() => setPending(null)}
        actions={<><button type="button" className="page-delete__cancel" onClick={() => setPending(null)}>Bỏ qua</button><button type="button" className="page-delete__confirm page-delete__confirm--neutral" onClick={confirmRemove}>Gỡ</button></>}
      >
        <p className="page-delete__copy">{pending ? `"${pending.title}" chỉ được gỡ khỏi dải bài của tiết này. Bài gốc, file tải lên và nét viết không bị xoá; mở lại bài sẽ đưa bài về dải.` : null}</p>
      </Modal>
    </div>
  );
}
